import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUIStore } from '../../stores/ui.store';
import { usePermissions } from '../../hooks/usePermissions';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../hooks/useAuth';
import { useThemeStore } from '../../stores/theme.store';
import { matchPath } from '../../lib/path-match';
import * as Icons from '@ant-design/icons';
import { Tooltip } from 'antd';

const BREAKPOINT_MD = 768;

export interface MenuItem {
	key: string;
	label: string;
	path: string;
	icon?: string;
	permissions?: string[];
	children?: MenuItem[];
}

export interface SidebarProps {
	menuConfig: MenuItem[];
}

export function Sidebar({ menuConfig }: SidebarProps) {
	const location = useLocation();
	const { sidebarCollapsed, toggleSidebar, mobileMenuOpen, setMobileMenuOpen } = useUIStore();
	const { hasPermission } = usePermissions();
	const { tenant } = useTenant();
	const { logout } = useAuth();
	const { mode } = useThemeStore();

	const [isMobile, setIsMobile] = useState(
		typeof window !== 'undefined' && window.innerWidth < BREAKPOINT_MD
	);
	const isDark = mode === 'dark';
	const collapsed = isMobile ? false : sidebarCollapsed;

	// Accordion: only one group open at a time
	const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

	// Auto-expand the group that contains the active route
	useEffect(() => {
		const activeGroup = menuConfig.find((item) => {
			if (!item.children?.length) return false;
			return item.children.some((sub) => matchPath(sub.path, location.pathname));
		});
		setExpandedGroup(activeGroup?.key ?? null);
	}, [location.pathname, menuConfig]);

	useEffect(() => {
		const handleResize = () => {
			const mobile = window.innerWidth < BREAKPOINT_MD;
			setIsMobile(mobile);
			if (!mobile) setMobileMenuOpen(false);
		};
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [setMobileMenuOpen]);

	useEffect(() => {
		setMobileMenuOpen(false);
	}, [location.pathname, setMobileMenuOpen]);

	useEffect(() => {
		if (isMobile && mobileMenuOpen) {
			const prev = document.body.style.overflow;
			document.body.style.overflow = 'hidden';
			return () => {
				document.body.style.overflow = prev;
			};
		}
	}, [isMobile, mobileMenuOpen]);

	// A menu item is visible when either:
	//   1. It has an own `permissions` list and the user holds at least one, OR
	//   2. It has children and at least one child is visible (recursively).
	// This makes parent groups automatically disappear when every child has
	// been permission-hidden — otherwise a Reseller Operator would see empty
	// "Tenants" or "Reports" headers with nothing under them.
	const isVisible = (item: MenuItem): boolean => {
		if (item.permissions?.length) {
			if (item.permissions.some(hasPermission)) return true;
			// Even if own perms fail, keep visible if any child qualifies.
		}
		if (item.children?.length) {
			return item.children.some(isVisible);
		}
		// No permissions declared and no children: leaf item without a gate.
		return !item.permissions?.length;
	};

	const accessibleItems = menuConfig.filter(isVisible);

	const renderMenuItem = (item: MenuItem) => {
		const isActive = matchPath(item.path, location.pathname);
		const IconComponent = item.icon
			? (Icons[item.icon as keyof typeof Icons] as React.ComponentType<any>)
			: Icons.AppstoreOutlined;

		const link = (
			<Link
				to={item.path}
				className={`
          group relative flex items-center gap-3 rounded-lg
          transition-all duration-200 no-underline
          ${collapsed ? 'mx-auto w-10 h-10 justify-center' : 'mx-3 px-2.5 py-2'}
          ${isActive
						? isDark
							? 'bg-white/[0.08] backdrop-blur-md ring-1 ring-white/10 !text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_2px_10px_-2px_rgba(0,0,0,0.4)]'
							: 'bg-white ring-1 ring-neutral-200/70 !text-neutral-950 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_-2px_rgba(15,23,42,0.08)]'
						: isDark
							? '!text-neutral-200 hover:!text-white hover:bg-white/[0.04]'
							: '!text-neutral-800 hover:!text-neutral-950 hover:bg-white/60'
					}
        `}
			>
				<IconComponent
					className={`text-[15px] shrink-0 ${isActive
							? isDark
								? 'text-white'
								: 'text-neutral-900'
							: isDark
								? 'text-neutral-400 group-hover:text-neutral-100'
								: 'text-neutral-700 group-hover:text-neutral-900'
						}`}
				/>
				{!collapsed && (
					<span
						className={`text-[13px] truncate whitespace-nowrap tracking-tight ${isActive ? 'font-bold' : 'font-semibold'}`}
					>
						{item.label}
					</span>
				)}
			</Link>
		);

		if (collapsed) {
			return (
				<Tooltip key={item.key} title={item.label} placement="right">
					{link}
				</Tooltip>
			);
		}

		return <div key={item.key}>{link}</div>;
	};

	const renderMenuItemOrGroup = (item: MenuItem) => {
		if (item.path.includes(':')) return null;

		const accessibleChildren = (item.children || [])
			.filter((sub) => !sub.path.includes(':'))
			.filter((sub) => {
				if (!sub.permissions?.length) return true;
				return sub.permissions.some(hasPermission);
			});

		if (accessibleChildren.length > 0) {
			const isAnyChildActive = accessibleChildren.some((sub) => matchPath(sub.path, location.pathname));
			const isActive = matchPath(item.path, location.pathname) || isAnyChildActive;
			const isExpanded = expandedGroup === item.key;

			return (
				<CollapsibleMenu
					key={item.key}
					groupKey={item.key}
					title={item.label}
					icon={item.icon || ''}
					isActive={isActive}
					isExpanded={isExpanded}
					onToggle={(key) => setExpandedGroup(expandedGroup === key ? null : key)}
					items={accessibleChildren}
					collapsed={collapsed}
					isDark={isDark}
					location={location}
				/>
			);
		}

		// If the item DEFINED children but NONE are accessible, the parent is
		// a group header with nothing underneath — do not fall through to
		// renderMenuItem (which would render it as a naked link and take the
		// user to a page they cannot see).
		if (item.children?.length) return null;

		return renderMenuItem(item);
	};

	return (
		<>
			{isMobile && (
				<div
					onClick={() => setMobileMenuOpen(false)}
					aria-hidden
					className={`fixed inset-0 z-30 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
						}`}
				/>
			)}

			<aside
				className={`
        fixed top-0 left-0 bottom-0
        transition-transform duration-200 ease-in-out
        flex flex-col z-40 bg-white
        ${isMobile ? 'w-62.5' : collapsed ? 'w-17' : 'w-62.5'}
        ${isMobile && !mobileMenuOpen ? '-translate-x-full' : 'translate-x-0'}
        ${isDark
						? 'bg-neutral-950 border-r border-neutral-800/80'
						: ''
					}
      `}
				// Light-mode sidebar: soft right-edge shadow instead of a thin
				// border, matching the Header's shadow-based divider. Uses the same
				// #fffdfd base as the rest of the shell so the sidebar reads as a
				// raised surface rather than a differently-tinted panel.
				style={
					!isDark
						? {
							backgroundColor: '#fffdfd',
							boxShadow:
								'1px 0 2px rgba(15, 23, 42, 0.04), 4px 0 12px -4px rgba(15, 23, 42, 0.06)',
						}
						: undefined
				}
			>
				{!isMobile && (
					<button
						onClick={toggleSidebar}
						className={`
            absolute top-4 -right-3 z-50
            w-6 h-6 rounded-full
            flex items-center justify-center
            border transition-all duration-150
            shadow-sm hover:shadow-md cursor-pointer
            ${isDark
								? 'bg-neutral-800 border-neutral-700 text-neutral-400 hover:text-white hover:bg-neutral-700'
								: 'bg-white border-neutral-200 text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
							}
          `}
						aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					>
						{collapsed ? (
							<Icons.RightOutlined style={{ fontSize: 10 }} />
						) : (
							<Icons.LeftOutlined style={{ fontSize: 10 }} />
						)}
					</button>
				)}

				{isMobile && (
					<button
						onClick={() => setMobileMenuOpen(false)}
						className={`
            absolute top-4 right-3 z-50
            w-7 h-7 rounded-md
            flex items-center justify-center
            transition-colors cursor-pointer
            ${isDark
								? 'text-neutral-400 hover:text-white hover:bg-white/5'
								: 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'
							}
          `}
						aria-label="Close menu"
					>
						<Icons.CloseOutlined style={{ fontSize: 14 }} />
					</button>
				)}

				{/* Header: Logo */}
				<div
					className={`h-16 flex items-center shrink-0 px-4 ${collapsed ? 'justify-center px-0' : 'gap-3'}`}
				>
					{tenant?.logo ? (
						<img
							src={tenant.logo}
							alt={tenant.name}
							className="h-9 w-9 rounded-xl object-contain shrink-0"
						/>
					) : (
						<div className="h-9 w-9 rounded-xl bg-gradient-to-br from-neutral-800 to-neutral-950 flex items-center justify-center shrink-0 shadow-sm shadow-black/20">
							<span className="text-white text-[15px] font-bold tracking-tight">
								{(tenant?.displayName || tenant?.name || 'K').charAt(0).toUpperCase()}
							</span>
						</div>
					)}
					{!collapsed && (
						<div className="flex flex-col min-w-0">
							<span
								className={`font-semibold text-[14px] leading-tight truncate tracking-tight ${isDark ? 'text-neutral-50' : 'text-neutral-900'
									}`}
							>
								{tenant?.displayName || tenant?.name}
							</span>
							<span
								className={`text-[11px] truncate ${isDark ? 'text-neutral-500' : 'text-neutral-400'
									}`}
							>
								Workspace
							</span>
						</div>
					)}
				</div>

				{/* Navigation */}
				<nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 space-y-0.5">
					{accessibleItems.map((item) => renderMenuItemOrGroup(item))}
				</nav>

				{/* Footer: Logout */}
				<div
					className={`shrink-0 ${isDark ? 'border-t border-neutral-800/80' : 'border-t border-neutral-100'}`}
				>
					<div className="py-2">
						{collapsed ? (
							<Tooltip title="Logout" placement="right">
								<button
									onClick={() => logout()}
									className={`mx-auto flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${isDark
											? 'text-neutral-200 hover:text-red-400 hover:bg-white/5'
											: 'text-neutral-800 hover:text-red-500 hover:bg-red-50'
										}`}
								>
									<Icons.LogoutOutlined className="text-[15px]" />
								</button>
							</Tooltip>
						) : (
							<button
								onClick={() => logout()}
								className={`group flex items-center gap-3 mx-3 px-2.5 py-2 rounded-lg transition-colors w-[calc(100%-24px)] ${isDark
										? 'text-neutral-200 hover:text-red-400 hover:bg-white/5'
										: 'text-neutral-900 hover:text-red-500 hover:bg-red-50'
									}`}
							>
								<Icons.LogoutOutlined
									className={`text-[15px] shrink-0 ${isDark ? 'text-neutral-300 group-hover:text-red-400' : 'text-neutral-800 group-hover:text-red-500'}`}
								/>
								<span className="text-[13px] font-semibold tracking-tight">Logout</span>
							</button>
						)}
					</div>
				</div>
			</aside>
		</>
	);
}

function CollapsibleMenu({
	groupKey,
	title,
	icon,
	isActive,
	isExpanded,
	onToggle,
	items,
	collapsed,
	isDark,
	location,
}: {
	groupKey: string;
	title: string;
	icon: string;
	isActive: boolean;
	isExpanded: boolean;
	onToggle: (key: string) => void;
	items: MenuItem[];
	collapsed: boolean;
	isDark: boolean;
	location: ReturnType<typeof useLocation>;
}) {
	const navigate = useNavigate();

	const handleParentClick = () => {
		if (!isExpanded) {
			onToggle(groupKey);
			navigate(items[0]?.path || '#');
		} else {
			onToggle(groupKey);
		}
	};

	const IconComponent = icon
		? (Icons[icon as keyof typeof Icons] as React.ComponentType<any>)
		: Icons.AppstoreOutlined;

	if (collapsed) {
		return (
			<Tooltip title={title} placement="right">
				<Link
					to={items[0]?.path || '#'}
					className={`
            mx-auto flex items-center justify-center w-10 h-10 rounded-lg
            transition-all duration-200 no-underline
            ${isActive
							? isDark
								? 'bg-white/[0.08] backdrop-blur-md ring-1 ring-white/10 !text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_2px_10px_-2px_rgba(0,0,0,0.4)]'
								: 'bg-white ring-1 ring-neutral-200/70 !text-neutral-900 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_-2px_rgba(15,23,42,0.08)]'
							: isDark
								? '!text-neutral-400 hover:!text-white hover:bg-white/[0.04]'
								: '!text-neutral-600 hover:!text-neutral-900 hover:bg-white/60'
						}
          `}
				>
					<IconComponent className="text-[15px]" />
				</Link>
			</Tooltip>
		);
	}

	return (
		<div>
			<button
				onClick={handleParentClick}
				className={`
          group relative flex items-center justify-between w-[calc(100%-24px)] mx-3 px-2.5 py-2 rounded-lg
          transition-colors duration-200 cursor-pointer
          ${isActive
						? isDark
							? 'text-white hover:bg-white/[0.04]'
							: 'text-neutral-950 hover:bg-white/60'
						: isDark
							? 'text-neutral-200 hover:text-white hover:bg-white/[0.04]'
							: 'text-neutral-800 hover:text-neutral-950 hover:bg-white/60'
					}
        `}
			>
				<div className="flex items-center gap-3 min-w-0">
					<IconComponent
						className={`text-[15px] shrink-0 ${isActive
								? isDark
									? 'text-white'
									: 'text-neutral-900'
								: isDark
									? 'text-neutral-400 group-hover:text-neutral-100'
									: 'text-neutral-700 group-hover:text-neutral-900'
							}`}
					/>
					<span
						className={`text-[13px] whitespace-nowrap tracking-tight truncate ${isActive ? 'font-bold' : 'font-semibold'}`}
					>
						{title}
					</span>
				</div>
				<Icons.RightOutlined
					className={`text-[9px] transition-transform duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'
						} ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}
				/>
			</button>

			<div
				className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-96 opacity-100 mt-0.5' : 'max-h-0 opacity-0'
					}`}
			>
				<div className="space-y-0.5 py-0.5">
					{items.map((child) => {
						const isChildActive = matchPath(child.path, location.pathname);
						const ChildIcon = child.icon
							? (Icons[child.icon as keyof typeof Icons] as React.ComponentType<
								Record<string, unknown>
							>)
							: Icons.AppstoreOutlined;

						return (
							<Link
								key={child.path}
								to={child.path}
								className={`
                  group flex items-center gap-3 ml-8 mr-3 px-2.5 py-1.5 rounded-md
                  transition-all duration-200 no-underline
                  ${isChildActive
										? isDark
											? 'bg-white/[0.08] backdrop-blur-md ring-1 ring-white/10 !text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_2px_10px_-2px_rgba(0,0,0,0.4)]'
											: 'bg-white ring-1 ring-neutral-200/70 !text-neutral-950 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_-2px_rgba(15,23,42,0.08)]'
										: isDark
											? '!text-neutral-300 hover:!text-white hover:bg-white/[0.04]'
											: '!text-neutral-700 hover:!text-neutral-950 hover:bg-white/60'
									}
                `}
							>
								<ChildIcon
									className={`text-[13px] shrink-0 ${isChildActive
											? isDark
												? 'text-white'
												: 'text-neutral-900'
											: isDark
												? 'text-neutral-500 group-hover:text-neutral-300'
												: 'text-neutral-500 group-hover:text-neutral-800'
										}`}
								/>
								<span
									className={`text-[12.5px] tracking-tight ${isChildActive ? 'font-bold' : 'font-semibold'}`}
								>
									{child.label}
								</span>
							</Link>
						);
					})}
				</div>
			</div>
		</div>
	);
}
